import { Controller } from "@hotwired/stimulus"
import { getCachedChats, getOutboxMessages } from "../chat_offline_resources"

// Hydrates the chat list from IndexedDB when offline.
// When online: no-op (server-rendered content is correct).
// When offline: reads cached_chats and renders cards.
export default class extends Controller {
  static targets = ["container", "emptyState"]

  async connect() {
    const isOffline = localStorage.getItem("chat:online") === "false" || !navigator.onLine
    if (!isOffline) return

    await this._hydrateFromIdb()
  }

  // -- private --

  async _hydrateFromIdb() {
    let chats
    try {
      chats = await getCachedChats()
    } catch {
      return
    }

    if (!this.hasContainerTarget) return

    if (chats.length === 0) {
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
      return
    }

    // Clear server-rendered content and replace with IDB content
    this.containerTarget.innerHTML = chats.map(chat => this._buildCard(chat)).join("")
  }

  _buildCard(chat) {
    const name = this._esc(chat.display_name || chat.name || "Chat")
    const unread = chat.unread_count || 0
    const unreadBadge = unread > 0
      ? `<span class="bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5 shrink-0 min-w-[20px] text-center">${unread > 99 ? "99+" : unread}</span>`
      : ""

    // Avatar initials
    const initials = name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?"

    // Timestamp (relative)
    const ts = chat.last_message_at ? this._relativeTime(new Date(chat.last_message_at)) : ""

    return `
      <div id="chat_${chat.id}_list_item">
        <a href="/chats/${chat.id}" class="flex items-center gap-3 px-4 py-4 border-b border-slate-50 active:bg-slate-100 min-h-[64px]">
          <div class="size-10 rounded-full bg-sapphire text-white flex items-center justify-center text-sm font-semibold shrink-0">
            ${initials}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2">
              <span class="font-semibold text-slate-800 truncate text-sm">${name}</span>
              <span class="text-xs text-slate-400 shrink-0">${this._esc(ts)}</span>
            </div>
            <div class="flex items-center justify-between gap-2 mt-0.5">
              <p class="text-sm text-slate-400">Offline</p>
              ${unreadBadge}
            </div>
          </div>
        </a>
      </div>
    `
  }

  _relativeTime(date) {
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1)  return "now"
    if (diffMins < 60) return `${diffMins}m`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d`
  }

  _esc(str) {
    return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
